import { faMinusCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Axios from "axios";
import { SubmitButton } from "Components/Button";
import { ButtonNav } from "Components/ButtonNav";
import { Horizontal } from "Components/Horizontal";
import { Section } from "Components/Section";
import { Checkbox } from "Inputs/Checkbox";
import { Dropdown, enumToDropdownOptions } from "Inputs/Dropdown";
import { EnterKeyCatcher } from "Inputs/EnterKeyCatcher";
import { FileInput } from "Inputs/FileInput";
import { useFormWithSchema, withFieldValueConverter } from "Inputs/FormTools";
import { DollarsAndCentsInput } from "Inputs/NumberInput";
import { Textbox } from "Inputs/Textbox";
import { ErrorMessage, YupDefined } from "Inputs/Tools";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { serverConstants } from "Shared/Constants";
import { groupBy, hasValue, resolveInOrder, sortBy } from "Tools";
import * as uuid from "uuid";
import * as Yup from "yup";
import { LcpPaths } from "./LcpPaths";
import { LcpAppPageWrapper } from "./LcpWorkflowPageWrapper";

export enum ClaimFileType {
  AdverseJudgementFile = "Certified copy of adverse judgment",
  LitigationCostInvoicesFile = "Invoice evidencing litigation cost",
  LitigationCostListingFile = "Line-item listing of litigation cost",
  ProofOfPaymentFile = "Proof of payment",
  InsuredW9File = "W-9 for the insured",
}

export function useComponentForm(props: {
  setGeneralSubmitError: (value: string | null) => void;
}) {
  const history = useHistory();
  return useFormWithSchema({
    initialValues: {
      policyNumber: null,
      contactName: null,
      contactEmail: null,
      confirmEmail: null,
      claimAmount: null,
      documentType: null,
      documents: [],
      hasAcknowledgedCriteria: false,
    },
    onSubmit: async (values, handlers) => {
      props.setGeneralSubmitError(null);
      const documentsByType = groupBy(values.documents, e => e.type);
      const documentTypesMissing = Object.values(ClaimFileType).filter(
        type => !documentsByType.has(type),
      );
      if (documentTypesMissing.length > 0) {
        return;
      }
      const documentIdsByType = new Map(
        await resolveInOrder(
          [...documentsByType],
          async ([type, documents]) => {
            const documentIds = await resolveInOrder(
              documents,
              async document => {
                const formData = new FormData();
                formData.append("file", document.file);
                try {
                  const result = await Axios.post<{ id: number }>(
                    "/api/FileClaim/Upload",
                    formData,
                  );
                  return result.data.id;
                } catch (e) {
                  props.setGeneralSubmitError(
                    `Failed to upload ${document.file.name}`,
                  );
                  throw e;
                }
              },
            );
            return [type, documentIds];
          },
        ),
      );
      try {
        await Axios.post("/api/FileClaim", {
          PolicyNumber: values.policyNumber,
          ContactName: values.contactName,
          EmailAddress: values.contactEmail,
          ClaimAmount: values.claimAmount,
          AdverseJudgementFileIds: documentIdsByType.get(
            ClaimFileType.AdverseJudgementFile,
          )!,
          LitigationCostListingFileIds: documentIdsByType.get(
            ClaimFileType.LitigationCostListingFile,
          )!,
          LitigationCostInvoicesFileIds: documentIdsByType.get(
            ClaimFileType.LitigationCostInvoicesFile,
          )!,
          ProofOfPaymentFileIds: documentIdsByType.get(
            ClaimFileType.ProofOfPaymentFile,
          )!,
          InsuredW9FileIds: documentIdsByType.get(ClaimFileType.InsuredW9File)!,
        });
      } catch (e: any) {
        if (e.response?.data?.policyNumberMessage === "notFound") {
          handlers.setFieldError(
            "policyNumber",
            "The policy number entered cannot be found.",
          );
          props.setGeneralSubmitError(
            "There were errors with this submission. Please correct the errors above and resubmit.",
          );
        } else {
          props.setGeneralSubmitError("Failed to file claim");
        }
        return;
      }
      history.push(LcpPaths.ClaimSubmitted);
    },
    validationSchema: Yup.object({
      policyNumber: YupDefined.string()
        .nullable()
        .label("Policy Number")
        .required(),
      contactName: YupDefined.string()
        .nullable()
        .label("Contact Name")
        .required(),
      contactEmail: YupDefined.string()
        .nullable()
        .label("Contact Email Address")
        .required(),
      confirmEmail: YupDefined.string()
        .nullable()
        .label("Confirm Email Address")
        .oneOf([Yup.ref("contactEmail")], "Email addresses must match")
        .required(),
      claimAmount: YupDefined.number()
        .nullable()
        .label("Claim Amount")
        .required(),
      documentType: YupDefined.enum(ClaimFileType)
        .nullable()
        .label("Document Type"),
      documents:
        YupDefined.ofType<{ id: string; type: ClaimFileType; file: File }[]>(),
      hasAcknowledgedCriteria: YupDefined.boolean()
        .label("Acknowledgement")
        .is([true], "Acknowledgement is required"),
    }),
  });
}

export function FileClaimPage() {
  const [generalSubmitError, setGeneralSubmitError] = useState<string | null>(
    null,
  );
  const form = useComponentForm({
    setGeneralSubmitError,
  });
  const documentTypeField = form.field.documentType;
  const documentsField = form.field.documents;
  const documentsByType = groupBy(documentsField.value, e => e.type);
  const documentTypesMissing = Object.values(ClaimFileType).filter(
    type => !documentsByType.has(type),
  );
  return (
    <LcpAppPageWrapper isWide documentTitle="File Claim">
      <Section title="File a Claim" className="narrowFieldBodies3">
        <EnterKeyCatcher handleSubmit={form.handleSubmit}>
          <Horizontal label="Policy Number" isRequired>
            <Textbox
              fieldProps={form.field.policyNumber}
              placeholder={`${serverConstants.InsuranceName} Policy Number, e.g. LCP000001-01`}
              style={{ flexGrow: 1 }}
            />
          </Horizontal>
          <Horizontal label="Contact Name" isRequired>
            <Textbox
              fieldProps={form.field.contactName}
              style={{ flexGrow: 1 }}
            />
          </Horizontal>
          <Horizontal label="Contact Email Address" isRequired>
            <Textbox
              fieldProps={form.field.contactEmail}
              style={{ flexGrow: 1 }}
            />
          </Horizontal>
          <Horizontal label="Confirm Email Address" isRequired>
            <Textbox
              fieldProps={form.field.confirmEmail}
              style={{ flexGrow: 1 }}
            />
          </Horizontal>
          <Horizontal label="Claim Amount" isRequired>
            <DollarsAndCentsInput
              field={form.field.claimAmount}
              style={{ flexGrow: 1 }}
            />
          </Horizontal>
          <div style={{ marginTop: "1em", marginBottom: "1em" }}>
            <strong>
              Please upload at least one of each of the following required claim
              documents:
            </strong>
            <ol style={{ marginLeft: "2em" }}>
              <li>Certified copy of the adverse judgment</li>
              <li>Detailed line-item listing of all litigation costs</li>
              <li>Invoices evidencing litigation costs</li>
              <li>Proof of payment</li>
              <li>W-9 for the insured</li>
            </ol>
            <strong>
              <em>Maximum file size for each upload: 2 MB</em>
            </strong>
          </div>
          <Horizontal label="Document Type">
            <Dropdown
              field={documentTypeField}
              options={enumToDropdownOptions(ClaimFileType)}
              placeholder="Select claim document to upload..."
            />
          </Horizontal>
          {hasValue(documentTypeField.value) && (
            <FileInput
              field={withFieldValueConverter(documentsField, {
                parse: (file: File | null) =>
                  file
                    ? [
                        ...documentsField.value,
                        {
                          id: uuid.v4(),
                          type: documentTypeField.value!,
                          file,
                        },
                      ]
                    : documentsField.value,
                format: () => null as File | null,
              })}
              validFileTypes={[
                ".pdf",
                ".doc",
                ".docx",
                ".xls",
                ".xlsx",
                ".csv",
              ]}
              maxSizeBytes={2 * 1024 * 1024}
            >
              Click here to upload: <strong>{documentTypeField.value}</strong>.{" "}
              <em>(PDF, .doc, .docx, .xls, .xlsx or .csv format)</em>
            </FileInput>
          )}
          {form.submitCount > 0 && documentTypesMissing.length > 0 && (
            <ErrorMessage>
              <div>
                Please upload at least one:
                <ul style={{ marginLeft: "2em", listStyleType: "disc" }}>
                  {documentTypesMissing.map(type => (
                    <li key={type}>{type}</li>
                  ))}
                </ul>
              </div>
            </ErrorMessage>
          )}
          <UploadedFileList
            files={[...documentsByType]
              .sort(sortBy(([type]) => type))
              .flatMap(([, documents]) =>
                documents.map((document, i) => ({
                  attachmentId: document.id,
                  filename: (
                    <>
                      {document.type} - {i + 1} <em>({document.file.name})</em>
                    </>
                  ),
                  onRemove: async () => {
                    await documentsField.onChange(
                      documentsField.value.filter(e => e.id !== document.id),
                    );
                  },
                })),
              )}
          />
          <div style={{ marginTop: "1em" }}>
            <div>
              <strong>
                I hereby represent that the information contained herein is
                true, accurate, and complete and that no material facts have
                been suppressed or misstated.
              </strong>
            </div>
            <br />
            <div>
              Notice: For coverage to be provided, a Notice of Claim via this
              form must be effectuated by you within forty-five (45) days
              following the date of an "Adverse Judgment".
            </div>
            <br />
          </div>
          <Checkbox field={form.field.hasAcknowledgedCriteria}>
            Click here to acknowledge that you have read and confirm the above
            statements.
          </Checkbox>
          <ButtonNav>
            <SubmitButton
              isSubmitting={form.isSubmitting}
              onClick={form.submitForm}
            >
              Submit Claim
            </SubmitButton>
          </ButtonNav>
          {hasValue(generalSubmitError) && (
            <div className="has-text-centered">
              <strong className="help is-danger" style={{ fontSize: "1rem" }}>
                {generalSubmitError}
              </strong>
            </div>
          )}
        </EnterKeyCatcher>
      </Section>
    </LcpAppPageWrapper>
  );
}

interface UploadedFile {
  filename: React.ReactElement | string;
  attachmentId: string;
  onRemove: () => void;
}

interface UploadedFileListProps {
  files: UploadedFile[];
}

export function UploadedFileList(props: UploadedFileListProps) {
  return (
    <>
      {props.files.map(document => (
        <div key={document.attachmentId} style={{ marginLeft: "0.5em" }}>
          <span
            className="click-catcher-small has-text-danger"
            style={{ marginRight: "0.5em" }}
            onClick={document.onRemove}
          >
            <FontAwesomeIcon icon={faMinusCircle} />
          </span>
          <span>
            <a href={`/api/AdminAttachment/${document.attachmentId}`}>
              {document.filename}
            </a>
          </span>
        </div>
      ))}
    </>
  );
}
